import React, { useCallback, useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import {
  ListSection,
  RecyclerList,
  Props as RecyclerListProps,
} from '@/components/recycler-list';
import { RefreshControl } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';
import {
  UnpaidItemType,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { useComponentFocus } from '@/hooks/use-component-focus';

const ESTIMATED_LIST_ITEM_HEIGHT = 70;

export type UnpaidItemListOption = ListOption<{
  unpaidItem: UnpaidItemType;
}>;

export type Props = {
  listSeparatorSpacing?: Spacing;
  renderListItem: (unpaidItem: UnpaidItemType) => React.ReactElement | null;
} & Omit<
  RecyclerListProps<UnpaidItemListOption>,
  'estimatedItemSize' | 'sections'
>;

export const UnpaidInvoiceList: React.FC<Props> = ({
  listSeparatorSpacing,
  renderListItem,
  ...rest
}) => {
  const [styles, theme] = useStyles();
  const {
    allUnpaidItems,
    ftniEnabled,
    markInvoicesViewed,
    refreshInvoices,
    refreshing,
    selectedInvoiceUUIDs,
    unlockExpiredFtniInvoices,
  } = usePaymentsContext();

  useComponentFocus({
    onFocus: useCallback(() => {
      markInvoicesViewed();
    }, [markInvoicesViewed]),
  });

  const sections = useMemo<ListSection<UnpaidItemListOption>[]>(() => {
    return [
      {
        data: (allUnpaidItems || []).map<UnpaidItemListOption>(
          (unpaidItem) => ({
            metadata: {
              unpaidItem: unpaidItem,
            },
            title: unpaidItem.displayNumber,
            value: unpaidItem.uuid,
          }),
        ),
        key: null,
      },
    ];
  }, [allUnpaidItems]);

  return (
    <RecyclerList
      estimatedItemSize={ESTIMATED_LIST_ITEM_HEIGHT}
      extraData={{ selectedInvoiceUUIDs }}
      ItemSeparatorComponent={() => (
        <ListItemSeparator
          style={[
            styles.listItemSeparator,
            ...(listSeparatorSpacing
              ? [{ marginHorizontal: theme.sk.spacing[listSeparatorSpacing] }]
              : []),
          ]}
        />
      )}
      refreshControl={
        <RefreshControl
          onRefresh={async () => {
            if (ftniEnabled) {
              await unlockExpiredFtniInvoices();
            }
            await refreshInvoices();
          }}
          refreshing={refreshing}
        />
      }
      renderItem={({ item }) => {
        const unpaidItem = item.metadata?.unpaidItem;
        if (!unpaidItem) {
          return null;
        }
        return renderListItem(unpaidItem);
      }}
      sections={sections}
      testID="unpaid-invoice-list"
      {...rest}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listItemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
